import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { dropLast, mergeDeepRight } from 'ramda';

import { actions } from '@ezugi/bootstrap';
import { GenericPayloadType, GenericStateType } from '@ezugi/types';

import { BL_TOTALS } from '../../constants/BackAndLay/betTypes';
import * as BET_TYPES from '../../constants/betTypes';
import { isBackAndLayOnURL } from '../../utils';
import { myBetsActions } from '../actions/myBets';
import { totalsActions } from '../actions/totals';
import { getTotalBet } from '../epics/bets/utils';

const {
  TOTAL_PLAYER_A_BACK, TOTAL_PLAYER_B_BACK, TOTAL_PLAYER_A_LAY, TOTAL_PLAYER_B_LAY,
} = BL_TOTALS;

const {
  TOTALS: { TOTAL_PLAYER_A, TOTAL_PLAYER_B, TOTAL_ONE_PAIR, TOTAL_STRAIGHT, TOTAL_FLUSH, TOTAL_STRAIGHT_FLUSH,
    TOTAL_THREE_OF_A_KIND },
} = BET_TYPES;

const BL_INITIAL_STATE_TOTALS = {
  totals: {
    // TOTALS FOR BACK BETS
    [TOTAL_PLAYER_A_BACK]: 0,
    [TOTAL_PLAYER_B_BACK]: 0,
    // TOTALS FOR LAY BETS
    [TOTAL_PLAYER_A_LAY]: 0,
    [TOTAL_PLAYER_B_LAY]: 0,
  },
};

const INITIAL_STATE_MY_BETS = {
  myBets: {},
};

const INITIAL_STATE_TOTALS = isBackAndLayOnURL ? BL_INITIAL_STATE_TOTALS : {
  totals: {
    // TOTALS FOR MAIN BETS
    [TOTAL_PLAYER_A]: 0,
    [TOTAL_PLAYER_B]: 0,
    // TOTALS FOR SIDE BETS
    [TOTAL_ONE_PAIR]: 0,
    [TOTAL_STRAIGHT]: 0,
    [TOTAL_FLUSH]: 0,
    [TOTAL_STRAIGHT_FLUSH]: 0,
    [TOTAL_THREE_OF_A_KIND]: 0,
  },
};

const { betActions: betActionsBootstrap } = actions;

export const INITIAL_STATE: GenericStateType = {
  current: {},
  totalBet: 0,
  history: [ {
    current: {},
    totalBet: 0,
  } ],
};

const FINAL_INITIAL_STATE: GenericStateType = {
  ...INITIAL_STATE,
  ...INITIAL_STATE_TOTALS,
  ...INITIAL_STATE_MY_BETS,
};

const betSlice = createSlice({
  name: 'bet',
  initialState: FINAL_INITIAL_STATE,
  reducers: {
    set({ history, ...state }, { payload: { type, value, ...rest } }: PayloadAction<GenericPayloadType>) {
      const current = {
        ...state.current,
        [type]: { value: ((state.current[type] || {}).value || 0) + value, ...rest },
      };
      const t = getTotalBet(current);

      return {
        ...state,
        current,
        history: [ ...history, { ...state, current, totalBet: t } ],
      };
    },
    reset(state) {
      return {
        ...state,
        ...FINAL_INITIAL_STATE,
      };
    },
    clear(state) {
      return {
        ...state,
        ...INITIAL_STATE,
        history: [ ...state.history, INITIAL_STATE ],
      };
    },
    cache(state, { payload }: PayloadAction<GenericPayloadType>) {
      return {
        ...state,
        last: payload,
      };
    },
    update(state, { payload }: PayloadAction<GenericPayloadType>) {
      return {
        ...state,
        ...payload,
        history: [ ...state.history, payload ],
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(betActionsBootstrap.history.reset, (state) => ({
      ...state,
      history: [ { ...INITIAL_STATE } ],
    }));
    builder.addCase(betActionsBootstrap.history.update, (state, { payload }: PayloadAction<GenericPayloadType>) => ({
      ...state,
      ...payload,
    }));
    builder.addCase(
      betActionsBootstrap.totalBet.set,
      (state, { payload: { value = 0 } }: PayloadAction<{ value: number }>) => ({
        ...state,
        totalBet: value,
      })
    );
    builder.addCase(myBetsActions.myBets.push, (
      { myBets, ...state },
      { payload }: PayloadAction<GenericPayloadType>
    ) => ({
      ...state,
      myBets: { ...myBets, ...payload },
    }));

    builder.addCase(myBetsActions.myBets.pop, (
      { myBets, ...state },
    ) => ({
      ...state,
      myBets: dropLast(1, myBets),
    }));

    builder.addCase(myBetsActions.myBets.clear, (
      state
    ) => ({
      ...state,
      ...INITIAL_STATE_MY_BETS,
    }));

    /* totals for each type of BET */
    builder.addCase(totalsActions.totals.set, (
      state,
      { payload }: PayloadAction<GenericPayloadType>
    ) => ({
      ...state,
      totals: { ...payload },
    }));

    builder.addCase(totalsActions.totals.clear, (
      state
    ) => ({
      ...state,
      ...INITIAL_STATE_TOTALS,
    }));
  },
});

export const betActions = mergeDeepRight(betActionsBootstrap, {
  bet: betSlice.actions,
});

export default betSlice.reducer;
